import { DatePipe, NgStyle } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { EmployeeRequestApprovalStatusDialogComponent } from 'src/app/components/main/shared/employee-request-approval-status-dialog/employee-request-approval-status-dialog.component';
import { ManageLocationDialogComponent } from 'src/app/components/main/shared/locations/manage-location-dialog/manage-location-dialog.component';
import { LocationDialogModeEnum } from 'src/app/enums/location-dialog-mode.enum';
import { EmployeeRequestResponsibleForAnswerStatusEnum } from 'src/app/enums/employee-request-responsible-for-answer-status.enum';
import { EmployeeRequestResponsibleForAnswersRequestableTypeEnum } from 'src/app/enums/employee-request-responsible-for-answers-requestable-type.enum';
import { ClockRegisterReviewRequest } from 'src/app/models/clock-register-review-request.model';
import { EmployeeRequestApprovalStatusDialogData } from 'src/app/models/employee-request-responsible-for-answer.model';
import { Location, ManageLocationDialogData } from 'src/app/models/location.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UserService } from 'src/app/services/user.service';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
  selector: 'app-clock-register-review-requests',
  templateUrl: './clock-register-review-requests.component.html',
  styleUrls: ['./clock-register-review-requests.component.css'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatPaginator,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    NgStyle,
    MatButton,
    MatIconButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatPrefix,
    MatTooltip,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    DatePipe,
  ],
})
export class ClockRegisterReviewRequestsComponent extends IndexTemplateComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  readonly employee_request_responsible_for_answer_status_enum = EmployeeRequestResponsibleForAnswerStatusEnum;

  readonly displayed_columns: string[] = [
    'clock_type',
    'id',
    'user_name',
    'justification',
    'original',
    'proposed',
    'difference',
    'actions',
  ];

  readonly data_source = new MatTableDataSource<ClockRegisterReviewRequestSerialized>([]);

  readonly st: Array<{
    name: string;
    status: EmployeeRequestResponsibleForAnswerStatusEnum;
  }> = [
    {
      name: 'Pendiente',
      status: EmployeeRequestResponsibleForAnswerStatusEnum.PENDING,
    },
    {
      name: 'Aceptado',
      status: EmployeeRequestResponsibleForAnswerStatusEnum.ACCEPTED,
    },
    {
      name: 'Rechazado',
      status: EmployeeRequestResponsibleForAnswerStatusEnum.REJECTED,
    },
  ];

  private readonly api = inject(ApiService);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly user = inject(UserService);

  protected readonly permissions = inject(PermissionsService);
  protected readonly dialog = inject(MatDialog);

  protected readonly current_user_id = this.user.getCurrentUser()?.id;
  protected readonly is_rrhh_or_admin = toSignal(this.permissions.validateDepartmentObservable(['direction', 'rrhh']), {
    initialValue: false,
  });

  constructor() {
    const fb = inject(UntypedFormBuilder);
    const router = inject(Router);
    const activated_route = inject(ActivatedRoute);
    const utils = inject(UtilsService);

    super(router, fb, activated_route, utils);

    const status_from_query = activated_route.snapshot.queryParamMap.get('status');
    const responsible_user_id_from_query = activated_route.snapshot.queryParamMap.get('responsible_user_id');

    const responsible_user_id =
      responsible_user_id_from_query === null
        ? (this.current_user_id ?? '')
        : responsible_user_id_from_query === ''
          ? ''
          : Number(responsible_user_id_from_query);

    this.filterFormExtraParams = {
      status: [status_from_query ?? EmployeeRequestResponsibleForAnswerStatusEnum.PENDING],
      clock_type: [activated_route.snapshot.queryParamMap.get('clock_type') ?? ''],
      user_id: [
        activated_route.snapshot.queryParamMap.get('user_id') !== null ? Number(activated_route.snapshot.queryParamMap.get('user_id')) : '',
      ],
      responsible_user_id: [responsible_user_id],
      with: ['original_location,proposed_location'],
      append: ['meta_data'],
    };

    if (responsible_user_id_from_query === null && this.current_user_id !== undefined) {
      void router.navigate([], {
        relativeTo: activated_route,
        queryParams: {
          responsible_user_id: this.current_user_id,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  override fetchData(): void {
    this.api
      .getClockRegisterReviewRequestsPaged({
        ...this.filterForm.value,
        order_by: 'created_at:desc',
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(data => {
        this.data_source.data = data.data.map(serializeClockRegisterReviewRequest);

        this.setMetadata(Number(data.meta.total), Number(data.meta.current_page) - 1, Number(data.meta.per_page));
      });
  }

  reject(id: number): void {
    if (!window.confirm('¿Estás seguro de que quieres rechazar esta solicitud?')) {
      return;
    }

    this.api
      .updateClockRegisterReviewRequest(id.toString(), {
        status: EmployeeRequestResponsibleForAnswerStatusEnum.REJECTED,
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.fetchData();
      });
  }

  approve(id: number): void {
    if (!window.confirm('¿Estás seguro de que quieres aprobar esta solicitud?')) {
      return;
    }

    this.api
      .updateClockRegisterReviewRequest(id.toString(), {
        status: EmployeeRequestResponsibleForAnswerStatusEnum.ACCEPTED,
      })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.fetchData();
      });
  }

  openApprovalStatusDialog(clock_register_review_request: ClockRegisterReviewRequestSerialized): void {
    this.dialog.open(EmployeeRequestApprovalStatusDialogComponent, {
      width: '800px',
      data: {
        requestable_type: EmployeeRequestResponsibleForAnswersRequestableTypeEnum.ClockRegisterReviewRequest,
        requestable_id: clock_register_review_request.id,
        requestable_status: clock_register_review_request.status,
      } as EmployeeRequestApprovalStatusDialogData,
    });
  }

  openLocationDialog(location: Location): void {
    this.dialog.open(ManageLocationDialogComponent, {
      data: {
        mode: LocationDialogModeEnum.View,
        location,
      } satisfies ManageLocationDialogData,
    });
  }
}

interface ClockRegisterReviewRequestSerialized extends ClockRegisterReviewRequest {
  has_date_changed: boolean;
  has_location_changed: boolean;
  date_difference: string | null;
}

function serializeClockRegisterReviewRequest(
  clock_register_review_request: ClockRegisterReviewRequest
): ClockRegisterReviewRequestSerialized {
  const has_date_changed = hasDateChanged(clock_register_review_request);
  const current_date = clock_register_review_request.meta_data?.current_date;

  return {
    ...clock_register_review_request,
    has_date_changed,
    has_location_changed: hasLocationChanged(clock_register_review_request),
    date_difference:
      has_date_changed && current_date != null && clock_register_review_request.proposed_date != null
        ? differenceBetweenDates(current_date, clock_register_review_request.proposed_date)
        : null,
  };
}

function hasDateChanged(clock_register_review_request: ClockRegisterReviewRequest): boolean {
  const current = clock_register_review_request.meta_data?.current_date;
  if (current == null || clock_register_review_request.proposed_date == null) {
    return false;
  }

  return new Date(current).getTime() !== new Date(clock_register_review_request.proposed_date).getTime();
}

function hasLocationChanged(clock_register_review_request: ClockRegisterReviewRequest): boolean {
  if (clock_register_review_request.proposed_location_id == null) {
    return false;
  }

  return (clock_register_review_request.original_location_id ?? null) !== clock_register_review_request.proposed_location_id;
}

function differenceBetweenDates(date1: string | Date, date2: string | Date): string {
  const first_date = new Date(date1);
  const second_date = new Date(date2);

  const difference_in_days = Math.floor(
    (Date.UTC(first_date.getFullYear(), first_date.getMonth(), first_date.getDate()) -
      Date.UTC(second_date.getFullYear(), second_date.getMonth(), second_date.getDate())) /
      (1000 * 60 * 60 * 24)
  );

  const difference_in_minutes = Math.round(Math.abs(first_date.getTime() - second_date.getTime()) / (1000 * 60));

  return (
    (first_date > second_date ? '- ' : '+ ') +
    (difference_in_days !== 0 ? `${difference_in_days} días ` : '') +
    `${difference_in_minutes} min`
  );
}
